import belag from '@contracts/forms/belag.v1.json'
import tiefbau from '@contracts/forms/tiefbau.v1.json'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PageHeader } from '@/components/page'
import type { EvaluationResult, FormDefinition } from '@/forms/types'
import { FormRenderer } from '@/forms/renderer/FormRenderer'
import { ResultPanel } from '@/forms/renderer/ResultPanel'
import { pickText } from '@/forms/renderer/helpers'
import { requireNav } from '@/nav/guard'

// Referenzformulare aus contracts/forms (Zappter-Übernahme). Veröffentlichte Versionen kommen später vom Server.
const DEFINITIONS = {
  tiefbau: tiefbau as unknown as FormDefinition,
  belag: belag as unknown as FormDefinition,
} as const
type FormKey = keyof typeof DEFINITIONS

export const Route = createFileRoute('/_app/admin/forms/preview')({
  beforeLoad: requireNav('forms'),
  validateSearch: z.object({ form: z.enum(['tiefbau', 'belag']).optional().catch(undefined) }),
  component: FormPreviewPage,
})

function FormPreviewPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage ?? 'en'
  const { form: formKey = 'tiefbau' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const definition = DEFINITIONS[formKey as FormKey]
  const [result, setResult] = useState<EvaluationResult | null>(null)

  return (
    <>
      <PageHeader title={t('forms.previewTitle')} description={t('forms.previewDescription')} />
      <div className="mb-6 grid max-w-sm gap-1.5">
        <label htmlFor="form-select" className="text-sm font-medium">
          {t('forms.selectForm')}
        </label>
        <select
          id="form-select"
          className="h-11 rounded-md border border-input bg-card px-3 text-base"
          value={formKey}
          onChange={(event) => void navigate({ search: { form: event.target.value as FormKey } })}
        >
          {(Object.keys(DEFINITIONS) as FormKey[]).map((key) => (
            <option key={key} value={key}>
              {pickText(DEFINITIONS[key].title, lang)} (
              {t('forms.version', { version: DEFINITIONS[key].version })})
            </option>
          ))}
        </select>
      </div>

      <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="rounded-lg border bg-card p-5">
          <h2 className="mb-1 text-xl font-semibold">{pickText(definition.title, lang)}</h2>
          {definition.description && (
            <p className="mb-6 text-muted-foreground">{pickText(definition.description, lang)}</p>
          )}
          {/* key: beim Formularwechsel frischen Zustand */}
          <FormRenderer key={formKey} definition={definition} onEvaluate={setResult} />
        </div>

        <div className="sticky top-4">
          {result && (
            <ResultPanel definition={definition} result={result} title={t('forms.resultArea')} />
          )}
        </div>
      </div>
    </>
  )
}
